import { type PropsWithChildren, type ReactNode } from 'react';
import {
  Button,
  type ButtonProps,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/index.ts';
import { cn } from '@/shared/utils';
import FailedStatusIcon from '@/shared/assets/failed-status-icon.svg?react';

type Props = {
  title: string;
  description?: string;
  buttonText?: string;
  buttonVariant?: ButtonProps['variant'];
  isSuccess: boolean;
  showButton?: boolean;
  onClick?: () => void;
  className?: string;
  icon?: ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
};

const ResultContent = ({
  isSuccess,
  onClick,
  title,
  description,
  buttonText,
  buttonVariant,
  children,
  showButton = true,
  className,
  icon,
  titleClassName,
  descriptionClassName,
}: PropsWithChildren<Props>) => {
  return (
    <div
      className={cn(
        'flex flex-col h-[43rem] max-w-[48rem] relative items-center',
        className
      )}>
      <div className="flex flex-col items-center justify-center gap-[2.4rem] flex-1 max-w-[30rem] py-[3.2rem]">
        <div className="flex items-center justify-center text-foreground">
          {icon ??
            (isSuccess ? (
              <img
                src="/assets/success-status-icon.svg"
                alt="Success"
                className="size-26"
              />
            ) : (
              <FailedStatusIcon className="size-26" />
            ))}
        </div>

        <DialogHeader className="flex flex-col items-center gap-[0.8rem]">
          <DialogTitle
            className={cn(
              'headline1 text-center text-foreground',
              titleClassName
            )}>
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription
              className={cn(
                'p3 text-center text-muted-foreground max-w-[40rem]',
                descriptionClassName
              )}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
      </div>

      {children}

      {showButton && (
        <Button
          onClick={onClick}
          variant={buttonVariant || (isSuccess ? 'secondary' : 'default')}
          size="lg"
          className="w-full headline4">
          {buttonText || ''}
        </Button>
      )}
    </div>
  );
};

export { ResultContent };
